import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, EmptyState, Field } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Coffee, Gift, CheckCircle2, Video } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null);
const localInput = (iso) => {
  const d = iso ? new Date(iso) : new Date(Date.now() + 86400000);
  d.setMinutes(iso ? d.getMinutes() : 0, 0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

/** Student view: respond to the campus lunch / check-ins assigned to them. */
export function MyTouchpoints() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [draft, setDraft] = useState({});
  const load = () => api.get("/my-touchpoints").then(({ data }) => setData(data)).catch(() => setData({ touchpoints: [], gift_cards: [] }));
  useEffect(() => { load(); }, []);

  const send = async (t) => {
    const d = draft[t.id] || {};
    if (!d.when) return toast.error("Pick a day and time that suits you");
    setBusy(t.id);
    try {
      await api.post(`/touchpoints/${t.id}/student-response`, {
        scheduled_at: new Date(d.when).toISOString(), gift_card_choice: d.gift || t.gift_card_choice || null,
      });
      toast.success("Sent to your Campus Coordinator");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const rows = data?.touchpoints || [];
  if (!data || rows.length === 0) return null;

  return (
    <Card title="Your campus touchpoints" data-testid="my-touchpoints-card">
      <p className="text-sm text-zinc-500 mb-3">Your Campus Coordinator would like to catch up with you. Pick a day and time that suits you — they'll confirm it.</p>
      <div className="space-y-3">
        {rows.map((t) => {
          const d = draft[t.id] || {};
          return (
            <div key={t.id} className="border rounded-md p-4" data-testid={`my-tp-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><Coffee className="w-4 h-4 text-cc-orange" />{t.type}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {t.coordinator_name ? `With ${t.coordinator_name}` : "With your Campus Coordinator"}
                    {t.scheduled_at ? ` · ${fmt(t.scheduled_at)}` : ""}
                  </p>
                  {t.gift_card_choice && (
                    <p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1.5">
                      <Gift className="w-3.5 h-3.5 text-cc-orange" />Gift card: {t.gift_card_choice}{t.gift_card_sent ? " · sent" : ""}
                    </p>
                  )}
                </div>
                <StatusBadge status={t.status} />
              </div>
              {t.status === "Scheduled" && t.daily_room_url && (
                <a href={t.daily_room_url} target="_blank" rel="noreferrer" className="inline-flex mt-3">
                  <Button size="sm" className="bg-cc-orange text-white h-7 text-xs" data-testid={`my-tp-join-${t.id}`}><Video className="w-3 h-3 mr-1" />Join</Button>
                </a>
              )}
              {["Pending", "Requested"].includes(t.status) && (
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <Field label="Day & time that suits you">
                    <Input type="datetime-local" value={d.when ?? localInput(t.scheduled_at)}
                      onChange={(e) => setDraft({ ...draft, [t.id]: { ...d, when: e.target.value } })}
                      data-testid={`my-tp-when-${t.id}`} />
                  </Field>
                  <Field label="Lunch gift card choice">
                    <Select value={d.gift ?? (t.gift_card_choice || "")} onValueChange={(v) => setDraft({ ...draft, [t.id]: { ...d, gift: v } })}>
                      <SelectTrigger data-testid={`my-tp-gift-${t.id}`}><SelectValue placeholder="Pick your gift card" /></SelectTrigger>
                      <SelectContent>{(data.gift_cards || []).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Button size="sm" disabled={busy === t.id} onClick={() => send(t)} className="bg-cc-green text-white h-8 text-xs" data-testid={`my-tp-send-${t.id}`}>
                      {t.status === "Requested" ? "Update my preference" : "Send my preference"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/** Coordinator view: the touchpoints assigned to them, with the two actions they owe the student. */
export function AssignedTouchpoints({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/my-assigned-touchpoints").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const act = async (t, fn, msg) => {
    setBusy(t.id);
    try { await fn(); toast.success(msg); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const giftsToSend = rows.filter((t) => t.gift_card_choice && !t.gift_card_sent).length;

  return (
    <Card title="Touchpoints to complete" data-testid="assigned-touchpoints-card"
      action={giftsToSend > 0 ? <span className="text-xs font-medium text-orange-700" data-testid="gifts-to-send-count">{giftsToSend} gift card{giftsToSend > 1 ? "s" : ""} to send</span> : null}>
      {rows.length === 0 ? <EmptyState text="No open touchpoints — you're all caught up." /> : (
        <div className="space-y-2">
          {rows.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 border rounded-md px-3 py-2.5" data-testid={`assigned-tp-${t.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-zinc-800">{t.student?.name || "Student"} · {t.type}</p>
                <p className="text-xs text-zinc-500">
                  {t.scheduled_at ? fmt(t.scheduled_at) : "No time set yet"}
                  {t.gift_card_choice ? ` · gift card: ${t.gift_card_choice}${t.gift_card_sent ? " (sent)" : ""}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusBadge status={t.status} />
                {t.gift_card_choice && !t.gift_card_sent && (
                  <Button size="sm" disabled={busy === t.id} className="bg-cc-orange text-white h-7 text-xs"
                    onClick={() => act(t, () => api.post(`/touchpoints/${t.id}/gift-card-sent`), `${t.gift_card_choice} gift card marked sent`)}
                    data-testid={`assigned-tp-gift-${t.id}`}><Gift className="w-3 h-3 mr-1" />Gift card sent</Button>
                )}
                <Button size="sm" variant="outline" disabled={busy === t.id} className="h-7 text-xs"
                  onClick={() => act(t, () => api.patch(`/touchpoints/${t.id}`, { status: "Completed" }), `${t.type} marked complete`)}
                  data-testid={`assigned-tp-complete-${t.id}`}><CheckCircle2 className="w-3 h-3 mr-1" />Mark complete</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
